import classNames from 'classnames';
import { addDays } from 'date-fns/addDays';
import { isPast } from 'date-fns/isPast';
import React, { useCallback, useMemo } from 'react';

import { getZonedDate } from '@eika-infrastruktur/date';
import { formatAccountNumber } from '@eika-infrastruktur/format';
import { Button, SmallButton } from '@eika/button';
import { ButtonWithSpinner } from '@eika/button-with-spinner';
import { getDateDisplayValue } from '@eika/calendar';
import { Checkbox } from '@eika/checkbox';
import { Icon, IconTypes } from '@eika/icon';
import { SuccessLabel, WarningLabel } from '@eika/label';

import { currencyFormatter } from '../../../../components/shared/numberFormat';
import type { Account, PaymentItemId, UniquePaymentId } from '../../../../generated-types';
import { useReceiver } from '../../../../hooks/useSetReceiver';
import useTranslation, { format } from '../../../../hooks/useTranslation';
import { OutboxWrapper } from '../../../../models/outbox';
import requireMultipleApprovers from '../../../../utils/boolean/requireMultipleApprovers';
import OutboxDetailsItem from './OutboxDetailsItem';
import OutboxFromAccountLabel from './OutboxFromAccountLabel';

import './OutboxView.scss';

type Props = {
    outboxListItem: OutboxWrapper;
    checked: boolean;
    handleCheckboxSwitch?(): void;
    onDeleteClick(): void;
    onUnapproveClick(): void;
    onEditClick(): void;
    disabled: boolean;
    signSingleItem(itemId: PaymentItemId, outboxId: UniquePaymentId): void;
    fromAccount: Account | undefined;
    isLoadingDetails: boolean;
};

const OutboxView: React.FC<Props> = ({
    outboxListItem,
    checked,
    handleCheckboxSwitch,
    onDeleteClick,
    onUnapproveClick,
    isLoadingDetails,
    onEditClick,
    disabled,
    signSingleItem,
    fromAccount,
}) => {
    const {
        receiver,
        currency,
        dueDate,
        permissions,
        approvers = [],
        probablyPSD2Payment,
        needSigningFromYou,
    } = outboxListItem;
    const translated = useTranslation();
    const isForeignPayment = outboxListItem.paymentItemType === 'FOREIGN_PAYMENT';
    const toAccountData = useReceiver(receiver?.accountNumber, isForeignPayment);
    const targetCurrency = currency ?? 'NOK';
    const zonedDate = getZonedDate(dueDate);

    const needsAdditionalSigning = requireMultipleApprovers(fromAccount);

    const approveOneItem = useCallback(async () => {
        signSingleItem(outboxListItem.paymentItemId, outboxListItem.uniqueId);
    }, [signSingleItem, outboxListItem.paymentItemId, outboxListItem.uniqueId]);

    const numberOfSigningsRequired = useMemo(() => {
        if (!fromAccount?.numberOfApprovers) return undefined;
        const number = fromAccount.numberOfApprovers - approvers.length;
        return Number.isNaN(number) ? undefined : number;
    }, [fromAccount, approvers]);

    return (
        <div className="outbox-item__container">
            <div
                data-testid="outbox-item"
                data-payment-id={String(outboxListItem.paymentItemId.value)}
                className={classNames('outbox-view__wrapper', {
                    'without-checkbox': !outboxListItem.permissions.canApprove,
                })}
            >
                {outboxListItem.permissions.canApprove && handleCheckboxSwitch && (
                    <div className="outbox-approval-list-item__checkbox">
                        <Checkbox
                            checked={checked}
                            onChange={handleCheckboxSwitch}
                            disabled={disabled}
                            data-testid="outbox-checkbox"
                            label={translated.outbox.checkboxLabel}
                            screenReaderOnlyLabel
                        />
                    </div>
                )}
                <dl className="approval-list-item-details-container">
                    <OutboxDetailsItem
                        data-testid="from-account"
                        label={translated.from}
                        additionalLabelInfo={
                            <OutboxFromAccountLabel targetAccount={fromAccount} outboxListItem={outboxListItem} />
                        }
                    >
                        <span className="fromaccount-name">{fromAccount?.alias}</span>
                        <span className="fromaccount-number">
                            {formatAccountNumber(outboxListItem.from?.accountNumber)}
                        </span>
                    </OutboxDetailsItem>
                    {isForeignPayment ? (
                        <>
                            <OutboxDetailsItem label={translated.to} data-testid="to-account">
                                <span className="toaccount-name">{outboxListItem.receiver.name}</span>
                                <span className="toaccount-number">{outboxListItem.receiver.iban?.value}</span>
                            </OutboxDetailsItem>
                        </>
                    ) : (
                        <>
                            <OutboxDetailsItem label={translated.to} data-testid="to-account">
                                <span className="toaccount-name">
                                    {toAccountData?.accountName ??
                                        formatAccountNumber(outboxListItem.receiver?.accountNumber) ??
                                        receiver.name}
                                </span>
                                <span className="toaccount-number">
                                    {!!toAccountData?.accountName &&
                                        formatAccountNumber(outboxListItem.receiver?.accountNumber)}
                                </span>
                            </OutboxDetailsItem>
                        </>
                    )}
                    <OutboxDetailsItem label={translated.payAmount} data-testid="amount">
                        <span className="amount">{currencyFormatter(outboxListItem.amount, targetCurrency)}</span>
                    </OutboxDetailsItem>
                </dl>
                <div className="additional-info">
                    {outboxListItem.kid && (
                        <div className="additional-info-item outbox-view__right-border">
                            <p className="additional-info-title">{translated.avtaleGiroAgreement.kidField}</p>
                            <p className="additional-info-value" data-testid="kid-value">
                                {outboxListItem.kid.value}
                            </p>
                        </div>
                    )}
                    {outboxListItem.note && (
                        <div className="additional-info-item outbox-view__right-border">
                            <p className="additional-info-title">{translated.avtaleGiroAgreement.noteText}</p>
                            <p className="additional-info-value" data-testid="note-value">
                                {outboxListItem.note}
                            </p>
                        </div>
                    )}
                    <div className="outbox-view__date-error-wrapper">
                        <div className="additional-info-item">
                            <p className="additional-info-title">{translated.outbox.plannedPaid}</p>
                            <p className="additional-info-value">
                                <span className="additional-info-value__field" data-testid="date-value">
                                    {isPast(addDays(zonedDate, 1)) ? (
                                        <s>{getDateDisplayValue(zonedDate)}</s>
                                    ) : (
                                        getDateDisplayValue(zonedDate)
                                    )}
                                </span>
                                {isPast(addDays(zonedDate, 1)) && (
                                    <span className="additional-info-value__suffix">{translated.dateField.today}</span>
                                )}
                            </p>
                        </div>
                    </div>
                </div>
                <div className="outbox-view__approval-item-button-wrapper">
                    {needsAdditionalSigning && typeof numberOfSigningsRequired === 'number' && (
                        <div className="outbox-view__approval-item__signing-warnings">
                            {approvers?.find((x) => x.loggedInUser) && (
                                <SuccessLabel
                                    role={undefined}
                                    aria-live={undefined}
                                    icon={IconTypes.USP_CHECKMARK_24}
                                    text={translated.outbox.youHaveApproved}
                                    data-testid="payment-approved-by-current-user"
                                />
                            )}
                            <WarningLabel
                                role={undefined}
                                aria-live={undefined}
                                data-testid="payment-needs-approval-from-others"
                                text={format(translated.outbox.waitingForNrOfSignings, numberOfSigningsRequired)}
                            />
                        </div>
                    )}
                    <div className="outbox-view__approval-item-buttons">
                        {permissions.canEdit && permissions.canReject && (
                            <ButtonWithSpinner
                                buttonType="negative"
                                size="small"
                                onClick={onEditClick}
                                showSpinner={isLoadingDetails}
                                disabled={disabled}
                                data-testid="outbox-edit-button"
                            >
                                <Icon type={IconTypes.PENN_24} className="icon--20" />
                                {translated.outbox.editOrDelete}
                            </ButtonWithSpinner>
                        )}
                        {permissions.canEdit && !permissions.canReject && (
                            <ButtonWithSpinner
                                buttonType="negative"
                                size="small"
                                onClick={onEditClick}
                                showSpinner={isLoadingDetails}
                                disabled={disabled}
                                data-testid="outbox-edit-button"
                            >
                                <Icon type={IconTypes.PENN_24} className="icon--20" />
                                {translated.outbox.edit}
                            </ButtonWithSpinner>
                        )}
                        {needSigningFromYou && !permissions.canEdit && permissions.canReject && (
                            <SmallButton
                                buttonType="negative"
                                disabled={disabled}
                                icon={IconTypes.SLETT_24}
                                onClick={onDeleteClick}
                                data-testid="outbox-delete-button"
                            >
                                {translated.outbox.deletePayment}
                            </SmallButton>
                        )}
                        {!needSigningFromYou && permissions.canUnapprove && (
                            <SmallButton
                                buttonType="negative"
                                disabled={disabled}
                                icon={IconTypes.KRYSS_24}
                                onClick={onUnapproveClick}
                                data-testid="outbox-unapprove-button"
                            >
                                {translated.outbox.removeApproval}
                            </SmallButton>
                        )}
                        {permissions.canApprove && (
                            <Button
                                buttonType="positive"
                                size="small"
                                disabled={disabled}
                                onClick={approveOneItem}
                                data-testid="outbox-approve-button"
                            >
                                <Icon type={IconTypes.CHECKMARK_LISTE_24} className="icon--20" />
                                {translated.outbox.approvePayment}
                            </Button>
                        )}
                        {!needSigningFromYou && permissions.canReject && (
                            <SmallButton
                                disabled={disabled}
                                icon={IconTypes.SLETT_24}
                                onClick={onDeleteClick}
                                data-testid="outbox-delete-button"
                            >
                                {translated.outbox.deletePayment}
                            </SmallButton>
                        )}
                        {probablyPSD2Payment && <WarningLabel text={translated.psd2Warning} />}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OutboxView;
